import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import { ActivityService } from 'services/crm'

const activitiesAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.dueAt < b.dueAt ? -1 : 1),
})

// Stable cache key per (dealId, leadId) tuple. `null` for an unscoped fetch.
const cacheKey = (params = {}) =>
  params.dealId != null
    ? `deal:${params.dealId}`
    : params.leadId != null
    ? `lead:${params.leadId}`
    : 'all'

export const fetchActivities = createAsyncThunk(
  'crm/activities/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await ActivityService.getAll(params)
      return { items: res.data, key: cacheKey(params) }
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching activities')
    }
  },
  {
    // Skip the network call if we've already loaded this scope. Multiple
    // mounted ActivityList instances (one per Tabs tab) share the cache.
    condition: (params, { getState }) => {
      const state = getState().crmActivities
      return !state.loadedKeys || !state.loadedKeys.includes(cacheKey(params))
    },
  },
)

export const createActivity = createAsyncThunk(
  'crm/activities/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await ActivityService.create(data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating activity')
    }
  },
)

export const updateActivity = createAsyncThunk(
  'crm/activities/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await ActivityService.update(id, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating activity')
    }
  },
)

export const removeActivity = createAsyncThunk(
  'crm/activities/remove',
  async (id, { rejectWithValue }) => {
    try {
      await ActivityService.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting activity')
    }
  },
)

const initialState = activitiesAdapter.getInitialState({
  loading: 'idle',
  loadedKeys: [],
})

const activitiesSlice = createSlice({
  name: 'crmActivities',
  initialState,
  reducers: {
    // Invalidate caches after a mutation forces a refetch
    invalidateActivities: (state) => {
      state.loadedKeys = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivities.fulfilled, (state, action) => {
        const { items, key } = action.payload
        activitiesAdapter.upsertMany(state, items)
        if (!state.loadedKeys.includes(key)) state.loadedKeys.push(key)
      })
      .addCase(createActivity.fulfilled, (state, action) => {
        activitiesAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateActivity.fulfilled, (state, action) => {
        activitiesAdapter.upsertOne(state, action.payload)
      })
      .addCase(removeActivity.fulfilled, (state, action) => {
        activitiesAdapter.removeOne(state, action.payload)
      })
  },
})

export const { invalidateActivities } = activitiesSlice.actions

const selectors = activitiesAdapter.getSelectors((state) => state.crmActivities)
export const selectAllActivities = selectors.selectAll
export const selectActivityById = selectors.selectById

export const selectActivitiesByDeal = (dealId) =>
  createSelector([selectAllActivities], (acts) => acts.filter((a) => a.dealId === dealId))

export const selectActivitiesByLead = (leadId) =>
  createSelector([selectAllActivities], (acts) => acts.filter((a) => a.leadId === leadId))

export const selectOverdueActivities = createSelector([selectAllActivities], (acts) => {
  const now = new Date().toISOString()
  return acts.filter((a) => !a.completedAt && a.dueAt < now)
})

export default activitiesSlice.reducer
